import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { TrendingDown, Shield, Target, BarChart3 } from "lucide-react";
import { SocialShare } from "@/components/SocialShare";

const RRange = () => {
  return (
    <div className="py-8">
      {/* Header */}
      <section className="bg-gradient-to-br from-primary to-accent text-primary-foreground py-16">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <Badge className="mb-4 bg-primary-foreground text-primary">Value Range</Badge>
            <h1 className="text-4xl md:text-5xl font-bold mb-6">R Range Portfolios</h1>
            <p className="text-xl text-primary-foreground/90 max-w-3xl mx-auto mb-4">
              Simpler Concept, Reduced Active Risk
            </p>
            <div className="flex justify-center items-center gap-8 text-lg mb-6">
              <div>
                <span className="font-semibold">AMC:</span> 0.20% p.a.
              </div>
              <div>
                <span className="font-semibold">Rebalancing:</span> Monthly
              </div>
            </div>
            <div className="flex justify-center">
              <SocialShare title="CBAM R Range Portfolios" variant="light" />
            </div>
          </div>
        </div>
      </section>

      {/* Portfolio Selection */}
      <section className="py-16 bg-background">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold mb-4">Choose Your R Range Portfolio</h2>
            <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
              Five OEIC-based portfolios with reduced active risk, designed to track benchmarks more closely while providing value through lower costs.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-16">
            <Card className="hover:shadow-lg transition-shadow">
              <CardHeader>
                <Badge className="w-fit mb-2 bg-green-100 text-green-800">Target Audience: Defensive</Badge>
                <CardTitle>R1 - Defensive</CardTitle>
                <CardDescription>Conservative OEIC-based approach</CardDescription>
              </CardHeader>
              <CardContent>
                <Button asChild className="w-full">
                  <Link to="/portfolios/r1">Learn More</Link>
                </Button>
              </CardContent>
            </Card>

            <Card className="hover:shadow-lg transition-shadow">
              <CardHeader>
                <Badge className="w-fit mb-2 bg-blue-100 text-blue-800">Target Audience: Cautious</Badge>
                <CardTitle>R2 - Cautious</CardTitle>
                <CardDescription>Measured growth with OEIC foundations</CardDescription>
              </CardHeader>
              <CardContent>
                <Button asChild className="w-full">
                  <Link to="/portfolios/r2">Learn More</Link>
                </Button>
              </CardContent>
            </Card>

            <Card className="hover:shadow-lg transition-shadow">
              <CardHeader>
                <Badge className="w-fit mb-2 bg-yellow-100 text-yellow-800">Target Audience: Balanced</Badge>
                <CardTitle>R3 - Balanced</CardTitle>
                <CardDescription>Balanced OEIC portfolio approach</CardDescription>
              </CardHeader>
              <CardContent>
                <Button asChild className="w-full">
                  <Link to="/portfolios/r3">Learn More</Link>
                </Button>
              </CardContent>
            </Card>

            <Card className="hover:shadow-lg transition-shadow">
              <CardHeader>
                <Badge className="w-fit mb-2 bg-orange-100 text-orange-800">Target Audience: Capital Growth</Badge>
                <CardTitle>R4 - Capital Growth</CardTitle>
                <CardDescription>Growth-oriented OEIC strategy</CardDescription>
              </CardHeader>
              <CardContent>
                <Button asChild className="w-full">
                  <Link to="/portfolios/r4">Learn More</Link>
                </Button>
              </CardContent>
            </Card>

            <Card className="hover:shadow-lg transition-shadow lg:col-start-2">
              <CardHeader>
                <Badge className="w-fit mb-2 bg-red-100 text-red-800">Target Audience: Aggressive</Badge>
                <CardTitle>R5 - Aggressive</CardTitle>
                <CardDescription>Maximum growth OEIC approach</CardDescription>
              </CardHeader>
              <CardContent>
                <Button asChild className="w-full">
                  <Link to="/portfolios/r5">Learn More</Link>
                </Button>
              </CardContent>
            </Card>
          </div>
          
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl font-bold mb-6">R Range Overview</h2>
              <p className="text-lg text-muted-foreground mb-6">
                The "R" class of portfolios will only hold Open Ended Investment Companies (OEICs). These are the funds that
                many retail clients are familiar with and can be readily held in many forms of Tax-wrapper, including Offshore Bonds.
              </p>
              <p className="text-muted-foreground mb-6">
                By only holding OEICs, the portfolio avoids incurring transactional costs from trading instruments such as
                Exchange-Traded-Funds (ETFs) or direct investments in equities or bonds.
              </p>
            </div>
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Target className="h-5 w-5" />
                  Key Features
                </CardTitle>
              </CardHeader>
              <CardContent>
                <ul className="space-y-3">
                  <li className="flex items-center">
                    <div className="w-2 h-2 bg-primary rounded-full mr-3" />
                    OEIC holdings only
                  </li>
                  <li className="flex items-center">
                    <div className="w-2 h-2 bg-primary rounded-full mr-3" />
                    Passive approach where possible
                  </li>
                  <li className="flex items-center">
                    <div className="w-2 h-2 bg-primary rounded-full mr-3" />
                    Benchmark sector tracking
                  </li>
                  <li className="flex items-center">
                    <div className="w-2 h-2 bg-primary rounded-full mr-3" />
                    Lower costs
                  </li>
                </ul>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Investment Approach */}
      <section className="py-16 bg-muted/50">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-center mb-12">Investment Approach</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <Card>
              <CardHeader>
                <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center mb-4">
                  <TrendingDown className="h-6 w-6 text-primary" />
                </div>
                <CardTitle>Reduced Active Risk</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-muted-foreground">
                  All funds (where possible) will be "passive" in nature to remove the risk of selecting underperforming
                  active fund managers.
                </p>
              </CardContent>
            </Card>
            <Card>
              <CardHeader>
                <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center mb-4">
                  <Shield className="h-6 w-6 text-primary" />
                </div>
                <CardTitle>Familiar Construct</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-muted-foreground">
                  Building from OEICs that many retail clients are familiar with, avoiding the need for Legal Entity
                  Identifiers and enabling use in various tax-wrappers.
                </p>
              </CardContent>
            </Card>
            <Card>
              <CardHeader>
                <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center mb-4">
                  <BarChart3 className="h-6 w-6 text-primary" />
                </div>
                <CardTitle>Tighter Tracking</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-muted-foreground">
                  Strict sectoral allocation within 2% of benchmark to build returns in excess of benchmark
                  while limiting downside risk.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-background">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <Card className="text-center">
            <CardContent className="p-8">
              <h3 className="text-2xl font-bold mb-4">Ready to Explore R Range Portfolios?</h3>
              <p className="text-muted-foreground mb-6">
                Discover how our OEIC-based portfolios can provide steady growth with reduced active risk and lower costs.
              </p>
              <Button size="lg" asChild>
                <Link to="/contact">Schedule a Consultation</Link>
              </Button>
            </CardContent>
          </Card>
        </div>
      </section>
    </div>
  );
};

export default RRange;